import '../../global/actions/initial';

import { memo } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';
import { callApi } from '../../api/gramjs/worker/connector';

import type { GlobalState } from '../../global/types';

import { DEBUG } from '../../config';
import { IS_TAURI } from '../../util/browser/globalEnvironment';
import { IS_MAC_OS, PLATFORM_ENV } from '../../util/browser/windowEnvironment';

import useCurrentOrPrev from '../../hooks/useCurrentOrPrev';
import useHistoryBack from '../../hooks/useHistoryBack';

import Transition from '../ui/Transition';
import AuthCode from './AuthCode.async';
import AuthPassword from './AuthPassword.async';
import AuthPhoneNumber from './AuthPhoneNumber';
import AuthQrCode from './AuthQrCode';
import AuthRegister from './AuthRegister.async';

import './Auth.scss';

type StateProps = {
  authState: GlobalState['auth']['state'];
};

const Auth = ({
  authState,
}: StateProps) => {
  const {
    returnToAuthPhoneNumber, goToAuthQrCode,
  } = getActions();

  const isMobile = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';

  const handleChangeAuthorizationMethod = () => {
    if (!isMobile) {
      goToAuthQrCode();
    } else {
      returnToAuthPhoneNumber();
    }
  };

  useHistoryBack({
    isActive: (!isMobile && authState === 'authorizationStateWaitPhoneNumber')
      || (isMobile && authState === 'authorizationStateWaitQrCode'),
    onBack: handleChangeAuthorizationMethod,
  });

  // For animation purposes
  const renderingAuthState = useCurrentOrPrev(
    authState !== 'authorizationStateReady' ? authState : undefined,
    true,
  );

  function getScreen() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return <AuthCode />;
      case 'authorizationStateWaitPassword':
        return <AuthPassword />;
      case 'authorizationStateWaitRegistration':
        return <AuthRegister />;
      case 'authorizationStateWaitPhoneNumber':
        return <AuthPhoneNumber />;
      case 'authorizationStateWaitQrCode':
        return <AuthQrCode />;
      default:
        return isMobile ? <AuthPhoneNumber /> : <AuthQrCode />;
    }
  }

  function getActiveKey() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return 0;
      case 'authorizationStateWaitPassword':
        return 1;
      case 'authorizationStateWaitRegistration':
        return 2;
      case 'authorizationStateWaitPhoneNumber':
        return 3;
      case 'authorizationStateWaitQrCode':
        return 4;
      default:
        return isMobile ? 3 : 4;
    }
  }

  const handleDebugLogin = () => {
    returnToAuthPhoneNumber();
    setTimeout(() => {
      getActions().setAuthRememberMe({ value: true });
      getActions().setAuthPhoneNumber({ phoneNumber: '+16502859925' });
    }, 500);
  };

  // Expose for CDP-based automated testing (CU agent)
  (window as any).__debugLogin = handleDebugLogin;

  // Event-driven auto-login: reacts to authState changes instead of setTimeout chains.
  // Stored on window so it persists across re-renders of this component.
  const pendingLogin = (window as any).__pendingPhoneLogin as
    { phone: string; code: string; password?: string } | undefined;

  if (pendingLogin && authState) {
    const phone = pendingLogin.phone.replace(/[^\d+]/g, '');
    const { code, password } = pendingLogin;

    if (authState === 'authorizationStateWaitPhoneNumber') {
      // GramJS is ready for phone number — provide it immediately
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitPhoneNumber → providing phone');
      delete (window as any).__pendingPhoneLogin;
      // Small delay to ensure the auth controller promise is set up
      setTimeout(() => {
        callApi('provideAuthPhoneNumber', phone.replace(/[^\d]/g, ''));
        // Re-store for next state transition
        (window as any).__pendingAutoCode = { code, password, phone };
      }, 500);
    } else if (authState === 'authorizationStateWaitCode' && (window as any).__pendingAutoCode) {
      const autoCode = (window as any).__pendingAutoCode;
      delete (window as any).__pendingAutoCode;
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitCode → providing code');
      setTimeout(() => {
        callApi('provideAuthCode', autoCode.code);
        if (autoCode.password) {
          (window as any).__pendingAutoPassword = autoCode.password;
        }
      }, 500);
    } else if (authState === 'authorizationStateWaitPassword' && (window as any).__pendingAutoPassword) {
      const pw = (window as any).__pendingAutoPassword;
      delete (window as any).__pendingAutoPassword;
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitPassword → providing password');
      setTimeout(() => getActions().checkPassword({ currentPassword: pw, onSuccess: () => {} }), 500);
    } else if (authState === 'authorizationStateWaitRegistration') {
      // New user — auto-register with phone tail as name
      // eslint-disable-next-line no-console
      console.log('[debug_login] authState=WaitRegistration → auto-registering');
      setTimeout(() => {
        callApi('provideAuthRegistration', {
          firstName: phone.replace(/\D/g, '').slice(-7), lastName: '',
        });
      }, 500);
    }
  }

  // Also handle the WaitCode/WaitPassword states when pendingLogin was already consumed
  if (!pendingLogin && authState === 'authorizationStateWaitCode' && (window as any).__pendingAutoCode) {
    const autoCode = (window as any).__pendingAutoCode;
    delete (window as any).__pendingAutoCode;
    // eslint-disable-next-line no-console
    console.log('[debug_login] authState=WaitCode → providing code (deferred)');
    setTimeout(() => {
      callApi('provideAuthCode', autoCode.code);
      if (autoCode.password) {
        (window as any).__pendingAutoPassword = autoCode.password;
      }
    }, 500);
  }
  if (!pendingLogin && authState === 'authorizationStateWaitPassword' && (window as any).__pendingAutoPassword) {
    const pw = (window as any).__pendingAutoPassword;
    delete (window as any).__pendingAutoPassword;
    // eslint-disable-next-line no-console
    console.log('[debug_login] authState=WaitPassword → providing password (deferred)');
    setTimeout(() => getActions().checkPassword({ currentPassword: pw, onSuccess: () => {} }), 500);
  }

  // Legacy: expose __debugLoginAs for CDP-based testing (non-event-driven fallback)
  (window as any).__debugLoginAs = (phone: string, code: string, password?: string) => {
    (window as any).__pendingPhoneLogin = { phone, code, password };
    // Trigger re-render to pick up the pending login
    returnToAuthPhoneNumber();
  };

  return (
    <Transition
      activeKey={getActiveKey()}
      name="fade"
      className="Auth"
      data-tauri-drag-region={IS_TAURI && IS_MAC_OS ? true : undefined}
    >
      {getScreen()}
      {DEBUG && (
        <div style="position:fixed;bottom:10px;right:10px;z-index:9999">
          <button
            id="debug-login"
            type="button"
            onClick={handleDebugLogin}
            style="padding:8px 16px;background:#e53935;color:white;border:none;border-radius:4px;cursor:pointer;font-size:12px"
          >
            Debug Login
          </button>
        </div>
      )}
    </Transition>
  );
};

export default memo(withGlobal(
  (global): Complete<StateProps> => {
    return {
      authState: global.auth.state,
    };
  },
)(Auth));
